import type * as React from 'react'

import clsx from 'clsx'

import type { WeeklyPostingStreakData } from '../hooks'
import { PlantAhead } from '../images/PlantAhead'
import { PlantAtRisk } from '../images/PlantAtRisk'
import { PlantHit } from '../images/PlantHit'
import { PlantOnTrack } from '../images/PlantOnTrack'
import { PlantOnTrackLarge } from '../images/PlantOnTrackLarge'
import { PlantStarted } from '../images/PlantStarted'
import { StreakBackgroundAtRisk } from '../images/StreakBackgroundAtRisk'
import { StreakBackgroundHit } from '../images/StreakBackgroundHit'

import styles from './StreakHeader.module.css'

import type { JSX } from 'react'

const StreakHeaderRoot = ({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}): JSX.Element => {
  return (
    <div className={clsx(styles.streakBackgroundContainer)} style={style}>
      {children}
    </div>
  )
}

const VARIANTS = {
  Default: StreakBackgroundHit,
  AtRisk: StreakBackgroundAtRisk,
  Hit: StreakBackgroundHit,
}

const StreakHeaderBackground = ({
  streak,
  children,
  ...rest
}: React.SVGProps<SVGSVGElement> & {
  streak?: WeeklyPostingStreakData['weeklyPostingStreak']
  children?: React.ReactNode
}): JSX.Element => {
  if (children) {
    return <div className={styles.streakBackground}>{children}</div>
  }

  let Background =
    streak && isVariantStatus(streak.status)
      ? VARIANTS[streak.status]
      : VARIANTS.Default

  if (streak && streak.status !== 'AtRisk' && (streak.periodsAhead ?? 0) > 0) {
    Background = VARIANTS.Default
  }

  return (
    <div className={styles.streakBackground}>
      <Background {...rest} className={styles.svgBackground} />
    </div>
  )
}

const STATUS_PLANT_MAP = {
  OnTrack: PlantOnTrack,
  Hit: PlantHit,
  Started: PlantStarted,
  AtRisk: PlantAtRisk,
  Ahead: PlantAhead,
}

function isVariantStatus(value: string): value is keyof typeof VARIANTS {
  return Object.hasOwn(VARIANTS, value)
}

function isPlantStatus(value: string): value is keyof typeof STATUS_PLANT_MAP {
  return Object.hasOwn(STATUS_PLANT_MAP, value)
}

const StreakHeaderContent = ({
  streak,
}: {
  streak?: WeeklyPostingStreakData['weeklyPostingStreak']
}): JSX.Element => {
  // Started
  if (streak?.status === 'Started' || !streak || streak.status === 'Missed') {
    return (
      <div className={styles.streakIllustrationContainer}>
        <img
          src="https://buffer-ui.s3.us-east-1.amazonaws.com/streaks/onboarding1.png"
          alt=""
          height={81}
        />
      </div>
    )
  }

  // Just started on track
  if (
    streak?.status === 'OnTrack' &&
    streak?.count === 0 &&
    streak?.periodsAhead === 0
  ) {
    return (
      <div className={styles.streakIllustrationContainer}>
        <div className={styles.imageContainer}>
          <div className={clsx(styles.mainCircle, styles.purple)}>
            <PlantOnTrackLarge />
          </div>
        </div>
      </div>
    )
  }

  let Plant =
    streak && isPlantStatus(streak.status)
      ? STATUS_PLANT_MAP[streak.status]
      : STATUS_PLANT_MAP.Started

  if (
    (streak?.status === 'Hit' || streak?.status === 'OnTrack') &&
    (streak?.periodsAhead ?? 0) > 0
  ) {
    Plant = PlantAhead
  }

  return (
    <div className={styles.streakIllustrationContainer}>
      <div className={styles.imageContainer}>
        <div
          className={clsx(styles.mainCircle, {
            [styles.atRisk]: streak && streak.status === 'AtRisk',
          })}
        >
          {streak?.count}
        </div>
        <div
          className={clsx(styles.secondaryCircle, {
            [styles.atRisk]: streak && streak.status === 'AtRisk',
          })}
        >
          <Plant />
        </div>
      </div>
    </div>
  )
}

export const StreakHeader = Object.assign(StreakHeaderRoot, {
  Background: StreakHeaderBackground,
  Content: StreakHeaderContent,
})
